import React, { useEffect, useState, useRef, useCallback } from 'react';
import './gallery.css';
import { motion } from 'framer-motion';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

export default function Gallery() {
  const [media, setMedia] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoScrollRef = useRef(null);
  const videoPlayingRef = useRef(false);
  const currentVideoRef = useRef(null);

  useEffect(() => {
    fetch('https://esmaaash.in/esmaaash_web/backend/api/get_gallery.php')
      .then((response) => response.json())
      .then((data) => setMedia(data))
      .catch((error) => console.error('Error fetching gallery:', error));
  }, []);

  const groupedMedia = media.reduce((groups, item) => {
    (groups[item.event_name] = groups[item.event_name] || []).push(item);
    return groups;
  }, {});

  const stopCurrentVideo = () => {
    if (currentVideoRef.current) {
      currentVideoRef.current.pause();
      currentVideoRef.current.currentTime = 0;
      currentVideoRef.current = null;
    }
  };

  const openModal = (eventName) => {
    setSelectedEvent(eventName);
    setCurrentIndex(0);
    videoPlayingRef.current = false;
    stopCurrentVideo();
  };

  const closeModal = () => {
    setSelectedEvent(null);
    clearInterval(autoScrollRef.current);
    stopCurrentVideo();
  };

  const checkIfVideoIsPlaying = useCallback((index) => {
    const currentMedia = groupedMedia[selectedEvent]?.[index];
    stopCurrentVideo();
    videoPlayingRef.current = currentMedia?.media_type === 'video';
  }, [groupedMedia, selectedEvent]);

  const nextMedia = () => {
    stopCurrentVideo();
    if (groupedMedia[selectedEvent]) {
      const nextIndex = (currentIndex + 1) % groupedMedia[selectedEvent].length;
      setCurrentIndex(nextIndex);
      checkIfVideoIsPlaying(nextIndex);
      clearInterval(autoScrollRef.current);
    }
  };

  const prevMedia = () => {
    stopCurrentVideo();
    if (groupedMedia[selectedEvent]) {
      const prevIndex =
        currentIndex === 0
          ? groupedMedia[selectedEvent].length - 1
          : currentIndex - 1;
      setCurrentIndex(prevIndex);
      checkIfVideoIsPlaying(prevIndex);
      clearInterval(autoScrollRef.current);
    }
  };

  useEffect(() => {
    clearInterval(autoScrollRef.current);

    if (
      selectedEvent &&
      groupedMedia[selectedEvent]?.length > 1 &&
      !videoPlayingRef.current
    ) {
      autoScrollRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          const nextIndex =
            (prevIndex + 1) % groupedMedia[selectedEvent].length;
          checkIfVideoIsPlaying(nextIndex);
          return nextIndex;
        });
      }, 4000);
    }

    return () => clearInterval(autoScrollRef.current);
  }, [selectedEvent, currentIndex, groupedMedia, checkIfVideoIsPlaying]);

  return (
    <div className="gallery-section">
      <motion.h2
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        data-text="GALLERY"
        className="text-3xl font-bold mt-16 mb-6"
      >
        GALLERY
      </motion.h2>

      <div className="gallery-container">
        {Object.entries(groupedMedia).map(([eventName, items]) => (
          <div key={eventName} className="event-wrapper">
            <motion.div
              className="gallery-item"
              whileHover={{ scale: 1.05 }}
              onClick={() => openModal(eventName)}
            >
              {items[0]?.media_type === 'image' && (
                <img
                  src={`https://esmaaash.in/esmaaash_web/uploads/gallery/${items[0].file_path}`}
                  alt={items[0].title || 'Event Image'}
                />
              )}
              {items[0]?.media_type === 'video' && (
                <video
                  src={`https://esmaaash.in/esmaaash_web/uploads/gallery/${items[0].file_path}`}
                  muted
                  autoPlay
                  loop
                />
              )}
            </motion.div>
            <div className="event-name">{eventName}</div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {selectedEvent && (
        <motion.div
          className="modal-overlay"
          onClick={closeModal}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
          >
            <FaTimes className="close-icon" onClick={closeModal} />
            <div className="media-container">
              {groupedMedia[selectedEvent]?.length > 1 && (
                <>
                  <FaChevronLeft className="nav-arrow left-arrow" onClick={prevMedia} />
                  <FaChevronRight className="nav-arrow right-arrow" onClick={nextMedia} />
                </>
              )}

              {/* Only render current media to avoid background playback */}
              {(() => {
                const item = groupedMedia[selectedEvent][currentIndex];
                if (!item) return null;

                return (
                  <div className="media-item active" key={item.id || currentIndex}>
                    {item.media_type === 'image' && (
                      <img
                        src={`https://esmaaash.in/esmaaash_web/uploads/gallery/${item.file_path}`}
                        alt={item.title || `Image ${currentIndex + 1}`}
                      />
                    )}

                    {item.media_type === 'video' && (
                      <video
                        ref={currentVideoRef}
                        src={`https://esmaaash.in/esmaaash_web/uploads/gallery/${item.file_path}`}
                        controls
                        autoPlay
                        onPlay={() => {
                          videoPlayingRef.current = true;
                          clearInterval(autoScrollRef.current);
                        }}
                        onEnded={() => {
                          videoPlayingRef.current = false;
                          autoScrollRef.current = setInterval(() => {
                            nextMedia();
                          }, 1000);
                        }}
                      />
                    )}

                    {item.media_type === 'youtube' && (
                      <iframe
                        src={item.youtube_link}
                        title={item.title || `YouTube ${currentIndex + 1}`}
                        frameBorder="0"
                        allowFullScreen
                      ></iframe>
                    )}
                  </div>
                );
              })()}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}